// src/api/payments.ts
import { isAxiosError } from "axios";
import api from "./client";

// ─── Stripe Account ────────────────────────────────────────────────────────────

export type StripeAccountStatus =
  | "onboarding_incomplete"
  | "pending_verification"
  | "enabled"
  | "restricted"
  | "disabled";

export interface PaymentAccount {
  payment_account_id: string;
  stripe_account_id: string;
  account_status: StripeAccountStatus;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted?: boolean;
  requirements_due: string[] | null;
  payments_admin_disabled: boolean;
}

export interface OnboardingLinkResponse {
  onboarding_url: string;
  stripe_account_id: string;
}

export interface DashboardLinkResponse {
  dashboard_url: string;
}

// ─── Payment Settings ──────────────────────────────────────────────────────────

export type PaymentMode = "disabled" | "deposit_required" | "full_payment_required";
export type DepositType = "fixed" | "percent";
export type CancellationWindow = 12 | 24 | 48 | 72;
export type RefundPolicy = "full_refund" | "no_refund";

export interface PaymentSettings {
  payment_settings_id?: string;
  payment_mode: PaymentMode;
  deposit_type: DepositType | null;
  deposit_value_minor_units: number | null;
  deposit_percent_bps: number | null;
  cancellation_policy_enabled: boolean;
  cancellation_window_hours: CancellationWindow | null;
  cancellation_policy_custom_note: string | null;
  deposit_refund_policy: RefundPolicy | null;
  fee_source: "plan" | "business_override" | "waived";
  payments_required: boolean;
  deposit_min_fixed_minor_units: number;
  deposit_min_percent_bps: number;
}

export interface PatchPaymentSettingsRequest {
  payment_mode?: PaymentMode;
  deposit_type?: DepositType;
  deposit_value_minor_units?: number;
  deposit_percent_bps?: number;
  cancellation_policy_enabled?: boolean;
  cancellation_window_hours?: CancellationWindow;
  cancellation_policy_custom_note?: string;
  deposit_refund_policy?: RefundPolicy;
}

// ─── API Client Functions ──────────────────────────────────────────────────────

/**
 * Returns null when the business has no Stripe account yet (API 404).
 * All other errors are re-thrown.
 */
export const getPaymentAccount = async (): Promise<PaymentAccount | null> => {
  try {
    const res = await api.get<PaymentAccount>("/portal/payments/account");
    return res.data;
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 404) {
      return null;
    }
    throw err;
  }
};

export const postOnboardingLink = async (
  return_url: string,
  refresh_url: string
): Promise<OnboardingLinkResponse> => {
  const res = await api.post<OnboardingLinkResponse>(
    "/portal/payments/account/onboarding-link",
    { return_url, refresh_url }
  );
  return res.data;
};

export const postDashboardLink = async (): Promise<DashboardLinkResponse> => {
  const res = await api.post<DashboardLinkResponse>(
    "/portal/payments/account/dashboard-link"
  );
  return res.data;
};

export const getPaymentSettings = async (): Promise<PaymentSettings> => {
  const res = await api.get<PaymentSettings>("/portal/payments/settings");
  return res.data;
};

export const patchPaymentSettings = async (
  payload: PatchPaymentSettingsRequest
): Promise<PaymentSettings> => {
  const res = await api.patch<PaymentSettings>("/portal/payments/settings", payload);
  return res.data;
};
